/**
 * Prototype `.req-modal-radios` row — mutually exclusive or multi-select chips.
 *
 * Rendered as bespoke chips (not the pill-shaped `SelectableChip`) to match the
 * prototype's `.req-modal-radio`: an 8px-radius rounded-rect with a muted idle
 * state and a soft blue-tint selected state (blue border + blue text on a light
 * blue wash) rather than a solid-blue fill with white text.
 *
 * @param {object} props
 * @param {string} [props.label]
 * @param {{ value: string, label: string }[]} props.options
 * @param {string|string[]|Record<string, boolean>} props.value
 * @param {(next: string) => void} props.onChange
 * @param {boolean} [props.multiple]
 */
const CHIP_BASE =
  'inline-flex items-center justify-center gap-1 rounded-lg border px-2.5 py-[5px] text-[11.5px] font-medium leading-none whitespace-nowrap select-none cursor-pointer transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40';

const CHIP_ON = 'border-blue-500/50 bg-blue-500/[0.12] text-blue-700 dark:text-blue-400';

const CHIP_OFF =
  'border-edge bg-black/[0.02] text-content-secondary hover:border-content-muted hover:text-content dark:bg-white/[0.03]';

function ChoiceChipGroup({ label, options, value, onChange, multiple = false }) {
  const isOn = (optionValue) => {
    if (multiple && value && typeof value === 'object' && !Array.isArray(value)) {
      return Boolean(value[optionValue]);
    }
    return value === optionValue;
  };

  return (
    <div className="flex flex-col gap-[5px]">
      {label ? (
        <span className="text-[11px] font-semibold text-content-secondary">{label}</span>
      ) : null}
      <div className="flex flex-wrap gap-[5px]">
        {options.map((option) => {
          const on = isOn(option.value);
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={on}
              onClick={() => onChange(option.value)}
              className={`${CHIP_BASE} ${on ? CHIP_ON : CHIP_OFF}`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default ChoiceChipGroup;
